import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import AppointmentStepper from "@/components/AppointmentStepper";
import MiniCalendar from "@/components/MiniCalendar";
import { useLang } from "@/contexts/LanguageContext";
import { createLead, getAppointmentSlots } from "@/lib/api";

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  project_type: "",
  budget: "",
  city: "",
  message: "",
  preferred_date: "",
  preferred_slot: "",
};

const INITIAL_STATUS = { loading: false, ok: false, err: "" };

function StepInfo({ form, onChange, t }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="appointment-step-0">
      <div>
        <label className="field-label">{t.contact.form.firstName}</label>
        <input name="first_name" value={form.first_name} onChange={onChange} required data-testid="appt-first-name" />
      </div>
      <div>
        <label className="field-label">{t.contact.form.lastName}</label>
        <input name="last_name" value={form.last_name} onChange={onChange} required data-testid="appt-last-name" />
      </div>
      <div>
        <label className="field-label">{t.contact.form.email}</label>
        <input type="email" name="email" value={form.email} onChange={onChange} required data-testid="appt-email" />
      </div>
      <div>
        <label className="field-label">{t.contact.form.phone}</label>
        <input name="phone" value={form.phone} onChange={onChange} data-testid="appt-phone" />
      </div>
    </div>
  );
}

function StepProject({ form, onChange, t }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="appointment-step-1">
      <div>
        <label className="field-label">{t.contact.form.type}</label>
        <select name="project_type" value={form.project_type} onChange={onChange} required data-testid="appt-project-type">
          <option value="">—</option>
          {t.contact.form.types.map((tp) => (
            <option key={tp} value={tp}>{tp}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="field-label">{t.contact.form.budget}</label>
        <select name="budget" value={form.budget} onChange={onChange} data-testid="appt-budget">
          <option value="">—</option>
          {t.contact.form.budgets.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="field-label">{t.contact.form.city}</label>
        <input name="city" value={form.city} onChange={onChange} data-testid="appt-city" />
      </div>
      <div className="md:col-span-2">
        <label className="field-label">{t.contact.form.message}</label>
        <textarea name="message" rows={4} value={form.message} onChange={onChange} data-testid="appt-message" />
      </div>
    </div>
  );
}

function StepSlots({ slots, selected, onSelect, date, t }) {
  return (
    <div data-testid="appointment-step-3">
      <p className="label-eyebrow mb-6">
        {t.appointment.step4} — {date}
      </p>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 max-w-2xl">
        {slots.map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => onSelect(s)}
            data-testid={`appt-slot-${s}`}
            className={`py-3 rounded-lg border text-sm transition-all duration-300 ${
              selected === s
                ? "bg-arven-gold text-arven-noir border-arven-gold"
                : "bg-transparent border-white/15 text-arven-offwhite/85 hover:border-arven-gold/60"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function Confirmation({ form, t, onReset }) {
  return (
    <div className="py-14 text-center" data-testid="appointment-confirmed">
      <CheckCircle2 size={48} className="text-arven-gold mx-auto" />
      <h2 className="serif text-3xl md:text-4xl mt-6">{t.appointment.confirm_h2}</h2>
      <p className="mt-4 max-w-md mx-auto text-arven-offwhite/70">{t.appointment.confirm_p}</p>
      <div className="mt-7 inline-flex flex-col items-center gap-1 text-[14px] text-arven-offwhite/80">
        <span>
          {form.first_name} {form.last_name} — {form.email}
        </span>
        <span className="text-arven-gold">
          {form.preferred_date} • {form.preferred_slot}
        </span>
      </div>
      <button onClick={onReset} className="btn-text-gold mt-8" data-testid="appt-new">
        {t.appointment.newRequest}
      </button>
    </div>
  );
}

export default function AppointmentPage() {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState(INITIAL_STATUS);

  const onChange = useCallback((e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }, []);

  const setVal = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const steps = [
    { id: "info", label: t.appointment.step1 },
    { id: "project", label: t.appointment.step2 },
    { id: "date", label: t.appointment.step3 },
    { id: "slot", label: t.appointment.step4 },
    { id: "confirm", label: t.appointment.step5 },
  ];

  // Only re-fetch when the selected date actually changes.
  useEffect(() => {
    if (!form.preferred_date) return;
    let cancelled = false;
    getAppointmentSlots(form.preferred_date)
      .then((d) => {
        if (!cancelled) setSlots(d.slots || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.preferred_date]);

  const canNext = () => {
    if (step === 0) return !!(form.first_name && form.last_name && form.email);
    if (step === 1) return !!form.project_type;
    if (step === 2) return !!form.preferred_date;
    if (step === 3) return !!form.preferred_slot;
    return true;
  };

  const submit = async () => {
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await createLead({ type: "appointment", locale: lang, source: "appointment-page", ...form });
      setStatus({ loading: false, ok: true, err: "" });
      setStep(4);
    } catch (_e) {
      setStatus({ loading: false, ok: false, err: t.common.error });
    }
  };

  const reset = () => {
    setForm(INITIAL_FORM);
    setStatus(INITIAL_STATUS);
    setStep(0);
  };

  return (
    <div data-testid="appointment-page">
      <Section className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{t.nav.cta}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6">
                {t.appointment.hero_h1}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.appointment.hero_sub}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Container className="pb-24">
        <AppointmentStepper steps={steps} current={step} />

        <div className="bg-arven-surface border border-white/10 rounded-2xl p-7 md:p-10">
          {step === 0 && <StepInfo form={form} onChange={onChange} t={t} />}
          {step === 1 && <StepProject form={form} onChange={onChange} t={t} />}
          {step === 2 && (
            <div data-testid="appointment-step-2">
              <p className="label-eyebrow mb-6">{t.appointment.step3}</p>
              <MiniCalendar
                value={form.preferred_date}
                onChange={(v) => setVal("preferred_date", v)}
              />
            </div>
          )}
          {step === 3 && (
            <StepSlots
              slots={slots}
              selected={form.preferred_slot}
              onSelect={(s) => setVal("preferred_slot", s)}
              date={form.preferred_date}
              t={t}
            />
          )}
          {step === 4 && status.ok && (
            <Confirmation form={form} t={t} onReset={reset} />
          )}

          {status.err && (
            <p className="text-[13px] text-[#E07A6A] mt-5">{status.err}</p>
          )}

          {step < 4 && (
            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className={`btn-text-gold ${step === 0 ? "opacity-30 pointer-events-none" : ""}`}
                data-testid="appt-back"
              >
                <ArrowLeft size={14} /> {t.common.back}
              </button>
              {step < 3 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(3, s + 1))}
                  disabled={!canNext()}
                  className={`btn-gold ${!canNext() ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="appt-next"
                >
                  {t.common.next} <ArrowRight size={14} />
                </button>
              )}
              {step === 3 && (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canNext() || status.loading}
                  className={`btn-gold ${(!canNext() || status.loading) ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="appt-submit"
                >
                  {status.loading ? t.common.sending : t.common.finish} <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
